/**
 * @license
 * Copyright 2024 takni.xyz
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { WagmiProvider } from 'wagmi'
import '@rainbow-me/rainbowkit/styles.css'
import { config } from './config/wagmi'
import { fetchAAConfigRelaunchCost } from './lib/aaConfigReads'
import App from './App.tsx'
import { ErrorBoundary } from './components/ErrorBoundary'
import { WalletConnectTracker } from './components/WalletConnectTracker'
import { FarcasterMiniAppBootstrap } from './components/FarcasterMiniAppBootstrap'
import { PasswordGate, isGateUnlocked, isGateEnabled } from './components/PasswordGate'
import './index.css'

/**
 * Global query defaults. Most queries set their own `staleTime`, but the wagmi
 * contract-read hooks (`useContractReads` on Home, ranking, per-card price) do
 * not — without a default they inherit `staleTime: 0` and refetch on every tab
 * focus/remount, so each connected user re-runs the full multicall stack
 * whenever they switch back to the tab. A modest default plus
 * `refetchOnWindowFocus: false` collapses that into the explicit polling the app
 * already does, cutting RPC fanout that scales linearly with concurrent users.
 * Queries that need fresher data still override `staleTime` locally.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
})

void queryClient.prefetchQuery({
  queryKey: ['aa-config', 'relaunch-cost'],
  queryFn: () => fetchAAConfigRelaunchCost(),
})

function GateWrapper({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState(() => !isGateEnabled())

  useEffect(() => {
    setUnlocked(isGateUnlocked())
  }, [])

  if (isGateEnabled() && !unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />
  }

  return <>{children}</>
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <GateWrapper>
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <WalletConnectTracker />
          <FarcasterMiniAppBootstrap>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          </FarcasterMiniAppBootstrap>
        </QueryClientProvider>
    </WagmiProvider>
    </GateWrapper>
  </React.StrictMode>,
) 